import React, { useState, createContext, useContext, Children } from 'react';
type Language = 'en' | 'es';
type Translations = {
  [key: string]: {
    en: string;
    es: string;
  };
};
const translations: Translations = {
  // Navigation
  'nav.services': {
    en: 'Services',
    es: 'Servicios'
  },
  'nav.about': {
    en: 'About',
    es: 'Nosotros'
  },
  'nav.insurance': {
    en: 'Insurance',
    es: 'Seguros'
  },
  'nav.contact': {
    en: 'Contact',
    es: 'Contacto'
  },
  'nav.call': {
    en: 'Call Now',
    es: 'Llamar Ahora'
  },
  // Hero
  'hero.title': {
    en: 'Compassionate. Professional. Culturally Responsive Care.',
    es: 'Atención Compasiva. Profesional. Culturalmente Responsable.'
  },
  'hero.subtitle': {
    en: 'Supporting your mental health at every stage of life. We are here when you need us most.',
    es: 'Apoyando su salud mental en cada etapa de la vida. Estamos aquí cuando más nos necesita.'
  },
  'hero.cta.request': {
    en: 'Request an Appointment',
    es: 'Solicitar Cita'
  },
  'hero.cta.call': {
    en: 'Call (610) 374-7035',
    es: 'Llamar (610) 374-7035'
  },
  'hero.badge': {
    en: 'Services available in English & Spanish',
    es: 'Servicios disponibles en Inglés y Español'
  },
  'hero.stat.patients': {
    en: 'Patients Served',
    es: 'Pacientes Atendidos'
  },
  'hero.stat.wait': {
    en: 'Avg. Wait Time',
    es: 'Tiempo de Espera'
  },
  'hero.stat.staff': {
    en: 'EN & ES Staff',
    es: 'Personal EN y ES'
  },
  // Services
  'services.title': {
    en: 'Our Services',
    es: 'Nuestros Servicios'
  },
  'services.subtitle': {
    en: 'Comprehensive mental health care tailored to your needs.',
    es: 'Atención integral de salud mental adaptada a sus necesidades.'
  },
  'service.psych': {
    en: 'Psychiatric Evaluations',
    es: 'Evaluaciones Psiquiátricas'
  },
  'service.meds': {
    en: 'Medication Management',
    es: 'Manejo de Medicamentos'
  },
  'service.therapy': {
    en: 'Individual Therapy',
    es: 'Terapia Individual'
  },
  'service.trauma': {
    en: 'Trauma-Focused Therapy',
    es: 'Terapia Centrada en Trauma'
  },
  'service.diagnosis': {
    en: 'Diagnosis & Ongoing Treatment',
    es: 'Diagnóstico y Tratamiento Continuo'
  },
  // Who We Help
  'who.title': {
    en: 'Who We Help',
    es: 'A Quién Ayudamos'
  },
  'who.children': {
    en: 'Children',
    es: 'Niños'
  },
  'who.adolescents': {
    en: 'Adolescents',
    es: 'Adolescentes'
  },
  'who.adults': {
    en: 'Adults',
    es: 'Adultos'
  },
  'who.families': {
    en: 'Families',
    es: 'Familias'
  },
  // Why Choose Us
  'why.title': {
    en: 'Why Choose Us',
    es: 'Por Qué Elegirnos'
  },
  'why.evidence': {
    en: 'Evidence-Based Care',
    es: 'Atención Basada en Evidencia'
  },
  'why.evidence.desc': {
    en: 'We use proven therapeutic methods to ensure the best outcomes for our patients.',
    es: 'Utilizamos métodos terapéuticos probados para asegurar los mejores resultados.'
  },
  'why.culture': {
    en: 'Culturally Responsive',
    es: 'Culturalmente Responsable'
  },
  'why.culture.desc': {
    en: 'We respect and understand diverse cultural backgrounds and values.',
    es: 'Respetamos y entendemos diversos orígenes y valores culturales.'
  },
  'why.bilingual': {
    en: 'Fully Bilingual Staff',
    es: 'Personal Totalmente Bilingüe'
  },
  'why.bilingual.desc': {
    en: 'Communication without barriers. We speak your language.',
    es: 'Comunicación sin barreras. Hablamos su idioma.'
  },
  'why.compassion': {
    en: 'Compassionate Environment',
    es: 'Ambiente Compasivo'
  },
  'why.compassion.desc': {
    en: 'A safe, welcoming space where you feel heard and valued.',
    es: 'Un espacio seguro y acogedor donde se siente escuchado y valorado.'
  },
  // Testimonials
  'testimonials.title': {
    en: 'Real People. Real Progress.',
    es: 'Personas Reales. Progreso Real.'
  },
  'testimonials.subtitle': {
    en: 'These are illustrative examples of the experiences our patients describe.',
    es: 'Estos son ejemplos ilustrativos de las experiencias que describen nuestros pacientes.'
  },
  'testimonials.1.quote': {
    en: 'The team here truly cares about your wellbeing. From my very first appointment, I felt heard, respected, and supported. This place changed my life.',
    es: 'El equipo aquí realmente se preocupa por tu bienestar. Desde mi primera cita, me sentí escuchado, respetado y apoyado. Este lugar cambió mi vida.'
  },
  'testimonials.1.author': {
    en: 'M. Rivera',
    es: 'M. Rivera'
  },
  'testimonials.2.quote': {
    en: 'Poder hablar con alguien en español hizo toda la diferencia. El equipo es increíblemente compasivo y profesional. Los recomiendo al 100%.',
    es: 'Poder hablar con alguien en español hizo toda la diferencia. El equipo es increíblemente compasivo y profesional. Los recomiendo al 100%.'
  },
  'testimonials.2.author': {
    en: 'L. González',
    es: 'L. González'
  },
  'testimonials.3.quote': {
    en: 'My daughter has made incredible progress since starting treatment here. The staff is patient, knowledgeable, and genuinely invested in her recovery.',
    es: 'Mi hija ha progresado increíblemente desde que comenzó el tratamiento aquí. El personal es paciente, conocedor y genuinamente comprometido con su recuperación.'
  },
  'testimonials.3.author': {
    en: 'A. Thompson',
    es: 'A. Thompson'
  },
  // Hours
  'hours.title': {
    en: 'Hours of Operation',
    es: 'Horario de Atención'
  },
  'hours.mon_fri': {
    en: 'Monday – Friday',
    es: 'Lunes – Viernes'
  },
  'hours.sat': {
    en: 'Saturday',
    es: 'Sábado'
  },
  'hours.sun': {
    en: 'Sunday',
    es: 'Domingo'
  },
  'hours.closed': {
    en: 'Closed',
    es: 'Cerrado'
  },
  // Contact
  'contact.title': {
    en: 'Get in Touch',
    es: 'Contáctenos'
  },
  'contact.subtitle': {
    en: 'We are here to help. Reach out to schedule an appointment.',
    es: 'Estamos aquí para ayudar. Contáctenos para programar una cita.'
  },
  'contact.form.name': {
    en: 'Full Name',
    es: 'Nombre Completo'
  },
  'contact.form.phone': {
    en: 'Phone Number',
    es: 'Número de Teléfono'
  },
  'contact.form.email': {
    en: 'Email Address',
    es: 'Correo Electrónico'
  },
  'contact.form.message': {
    en: 'Message',
    es: 'Mensaje'
  },
  'contact.form.submit': {
    en: 'Send Message',
    es: 'Enviar Mensaje'
  },
  'contact.emergency': {
    en: 'If this is a medical emergency, please call 911 immediately.',
    es: 'Si esto es una emergencia médica, llame al 911 inmediatamente.'
  },
  // Location
  'location.title': {
    en: 'Our Location',
    es: 'Nuestra Ubicación'
  },
  'location.directions': {
    en: 'Get Directions',
    es: 'Cómo Llegar'
  },
  'location.call': {
    en: 'Call Us',
    es: 'Llámenos'
  }
};
interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lang, setLang] = useState<Language>('en');
  const t = (key: string): string => {
    if (!translations[key]) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return translations[key][lang];
  };
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-158">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}